// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type {
  AddReactionData,
  AddReactionResponses,
  BulkUpdateIssueLinksByResourceTypeData,
  BulkUpdateIssueLinksByResourceTypeErrors,
  BulkUpdateIssueLinksByResourceTypeResponses,
  CreateIconData,
  CreateIconResponses,
  CreateIssueData,
  CreateIssueErrors,
  CreateIssueLinkData,
  CreateIssueLinkResponses,
  CreateIssueResponses,
  DeleteContextIssueLinkData,
  DeleteContextIssueLinkResponses,
  DeleteIconByIdData,
  DeleteIconByIdResponses,
  DeleteIssuesByIdData,
  DeleteIssuesByIdErrors,
  DeleteIssuesByIdResponses,
  DeleteReactionData,
  DeleteReactionResponses,
  GetBulkIssueAccessData,
  GetBulkIssueAccessErrors,
  GetBulkIssueAccessResponses,
  GetConfigurationData,
  GetConfigurationResponses,
  GetContextIssueLinkByIdData,
  GetContextIssueLinkByIdResponses,
  GetContextIssueLinksData,
  GetContextIssueLinksResponses,
  GetDefaultConfigurationData,
  GetDefaultConfigurationResponses,
  GetDefaultIconsData,
  GetDefaultIconsResponses,
  GetIssueAccessByIdData,
  GetIssueAccessByIdErrors,
  GetIssueAccessByIdResponses,
  GetIssueByIdData,
  GetIssueByIdErrors,
  GetIssueByIdResponses,
  GetIssuePermissionsData,
  GetIssuePermissionsErrors,
  GetIssuePermissionsResponses,
  GetLinkChangesData,
  GetLinkChangesResponses,
  GetLinkObjectsData,
  GetLinkObjectsResponses,
  ListChangesInContextData,
  ListChangesInContextErrors,
  ListChangesInContextResponses,
  ListIconsData,
  ListIconsResponses,
  ListIssuesData,
  ListIssuesErrors,
  ListIssuesResponses,
  ListObjectsInContextData,
  ListObjectsInContextErrors,
  ListObjectsInContextResponses,
  ListReactionsData,
  ListReactionsResponses,
  PatchIssuePermissionsData,
  PatchIssuePermissionsErrors,
  PatchIssuePermissionsResponses,
  UpdateConfigurationData,
  UpdateConfigurationResponses,
  UpdateContextIssueLinkData,
  UpdateContextIssueLinkResponses,
  UpdateIssuesByIdData,
  UpdateIssuesByIdErrors,
  UpdateIssuesByIdResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = Options2<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Delete an issue
 *
 * Delete a specific issue from a context.
 *
 * **Required action:** `issue:Delete` in `_actions`.
 *
 * Who has `issue:Delete`:
 * - PROJECT context restriction OFF: project ADMIN and issue creator only (assignees do **not** have Delete)
 * - PROJECT context restriction ON: project ADMIN and creator only; the `edit` ACL bucket does **not** include `issue:Delete`
 * - FILE context: all users who can access the file
 */
export const deleteIssuesById = <ThrowOnError extends boolean = false>(
  options: Options<DeleteIssuesByIdData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteIssuesByIdResponses,
    DeleteIssuesByIdErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}',
    ...options,
  });

/**
 * Get a single issue by ID
 *
 * Retrieve the details of a specific issue by its unique identifier. Returns minimal information by default. Additional fields can be requested via the fields query parameter. Use the includeDeleted parameter to include soft-deleted issues.
 *
 * **Required action:** `issue:Read` in `_actions`.
 *
 * - PROJECT context restriction OFF: project ADMINs, creators, assignees, and plain project members all have `issue:Read`
 * - PROJECT context restriction ON: only actors whose effective ACL bucket includes `issue:Read` (ADMIN, creator, assignee, explicit `edit`/`view` holders, `tc-groups:*` in view); actors with no ACL match receive 403
 * - FILE context: all users who can access the file receive `issue:Read`
 */
export const getIssueById = <ThrowOnError extends boolean = false>(
  options: Options<GetIssueByIdData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetIssueByIdResponses,
    GetIssueByIdErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}',
    ...options,
  });

/**
 * Update an existing issue
 *
 * Update the details of an existing issue within a specified context.
 *
 * **Required action:** `issue:Update` in `_actions`.
 *
 * Who has `issue:Update`:
 * - PROJECT context restriction OFF: project ADMIN, issue creator, assignee
 * - PROJECT context restriction ON: project ADMIN, creator, assignee, actors with explicit `edit` ACL bucket
 * - FILE context: all users who can access the file
 *
 * Include `?fields=_actions` to receive the updated `_actions` list.
 */
export const updateIssuesById = <ThrowOnError extends boolean = false>(
  options: Options<UpdateIssuesByIdData, ThrowOnError>
) =>
  (options.client ?? client).put<
    UpdateIssuesByIdResponses,
    UpdateIssuesByIdErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete the link for a specified context and issue
 *
 * Delete the link for a specified context and issue
 */
export const deleteContextIssueLink = <ThrowOnError extends boolean = false>(
  options: Options<DeleteContextIssueLinkData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteContextIssueLinkResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links/{linkId}',
    ...options,
  });

/**
 * Get a specific link by ID for a specified context and issue
 *
 * Retrieve a specific link by its ID for a specified context and issue
 */
export const getContextIssueLinkById = <ThrowOnError extends boolean = false>(
  options: Options<GetContextIssueLinkByIdData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetContextIssueLinkByIdResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links/{linkId}',
    ...options,
  });

/**
 * Update a specific link for a specified context and issue
 *
 * Update a specific link by its ID for a specified context and issue
 */
export const updateContextIssueLink = <ThrowOnError extends boolean = false>(
  options: Options<UpdateContextIssueLinkData, ThrowOnError>
) =>
  (options.client ?? client).put<
    UpdateContextIssueLinkResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links/{linkId}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Bulk update links by resource type for a specified context and issue
 *
 * Replace all links of a specific resource type for an issue. Existing links of the specified resource type will be deleted and replaced with the new links. Maximum of 500 links per resource type are allowed in a single request.
 */
export const bulkUpdateIssueLinksByResourceType = <
  ThrowOnError extends boolean = false,
>(
  options: Options<BulkUpdateIssueLinksByResourceTypeData, ThrowOnError>
) =>
  (options.client ?? client).put<
    BulkUpdateIssueLinksByResourceTypeResponses,
    BulkUpdateIssueLinksByResourceTypeErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links/bulk',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Get Issue configuration for the context
 *
 * Retrieve the configuration settings for a specified context. API will return default configuration if no configuration is set for the context.
 */
export const getConfiguration = <ThrowOnError extends boolean = false>(
  options: Options<GetConfigurationData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetConfigurationResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/configuration',
    ...options,
  });

/**
 * Update Issue configuration for the context
 *
 * Update the configuration settings for a specified context. Note that custom icons must be stored separately using the storeIcon API and referred here via iconId.
 */
export const updateConfiguration = <ThrowOnError extends boolean = false>(
  options: Options<UpdateConfigurationData, ThrowOnError>
) =>
  (options.client ?? client).put<
    UpdateConfigurationResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/configuration',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List issues within a specified context
 *
 * Retrieve a list of active (non-deleted) issues within a specified context using cursor-based pagination. Clients must paginate until `links.next` is null.
 *
 * **Visibility rules:**
 * - PROJECT context restriction OFF: all issues in the context are returned
 * - PROJECT context restriction ON: only issues for which the caller has `issue:Read` in their effective `_actions` are returned; issues with no ACL match are silently omitted
 * - FILE context: all issues in the context are returned
 */
export const listIssues = <ThrowOnError extends boolean = false>(
  options: Options<ListIssuesData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListIssuesResponses,
    ListIssuesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues',
    ...options,
  });

/**
 * Create a new issue within a specified context
 *
 * Create a new issue within a specified context (project or file TRN).
 *
 * **ACL behaviour on creation:**
 * - PROJECT context: when `shareNewIssuesWithAllProjectMembers` is `true` (default), `tc-groups:*` is auto-added to the `view` ACL bucket so all project members receive at least `issue:Read` access. When `false`, the issue is private — visible only to project ADMINs, the creator, and explicit ACL holders.
 * - FILE context: no ACL is stored; all project members who can access the file receive all issue actions except `issue:UpdatePermissions`.
 *
 * Include `?fields=_actions` to receive the caller's allowed actions on the newly created issue.
 */
export const createIssue = <ThrowOnError extends boolean = false>(
  options: Options<CreateIssueData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateIssueResponses,
    CreateIssueErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List reactions for an issue
 *
 * Retrieve paginated list of reactions for a specified issue
 */
export const listReactions = <ThrowOnError extends boolean = false>(
  options: Options<ListReactionsData, ThrowOnError>
) =>
  (options.client ?? client).get<ListReactionsResponses, unknown, ThrowOnError>(
    {
      security: [{ scheme: 'bearer', type: 'http' }],
      url: '/context/{contextId}/issues/{issueId}/reactions',
      ...options,
    }
  );

/**
 * Add a reaction to an issue
 *
 * Add a reaction (emoji) to a specified issue
 */
export const addReaction = <ThrowOnError extends boolean = false>(
  options: Options<AddReactionData, ThrowOnError>
) =>
  (options.client ?? client).post<AddReactionResponses, unknown, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/reactions',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List Issue Links
 *
 * Retrieve the list of links for a specified issue in a context
 */
export const getContextIssueLinks = <ThrowOnError extends boolean = false>(
  options: Options<GetContextIssueLinksData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetContextIssueLinksResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links',
    ...options,
  });

/**
 * Create a new link within a specified context and issue
 *
 * Create a new link within a specified context and issue. Maximum of 500 links per resource type are allowed per issue.
 */
export const createIssueLink = <ThrowOnError extends boolean = false>(
  options: Options<CreateIssueLinkData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateIssueLinkResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/links',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List custom icons within a specified context
 *
 * Retrieve a list of custom icons for a specified context. Maximum page size limit is 200.
 */
export const listIcons = <ThrowOnError extends boolean = false>(
  options: Options<ListIconsData, ThrowOnError>
) =>
  (options.client ?? client).get<ListIconsResponses, unknown, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/icons',
    ...options,
  });

/**
 * Create custom icon for a context
 *
 * Create a new custom icon for a specified context. Maximum of only 200 icons can be stored per context.
 */
export const createIcon = <ThrowOnError extends boolean = false>(
  options: Options<CreateIconData, ThrowOnError>
) =>
  (options.client ?? client).post<CreateIconResponses, unknown, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/icons',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Get user access information for multiple issues in bulk
 *
 * Retrieve the user access information (effective `_actions`) for multiple issues within a specified context.
 *
 * - The `_actions` array in each result reflects the active restriction mode at call time.
 * - Supports checking access for the current user or for specific actors (requires APP_ADMIN access).
 * - PROJECT context restriction ON: actors with no ACL match receive an empty `_actions` array.
 * - FILE context: returns all actions except `issue:UpdatePermissions` for each issue.
 */
export const getBulkIssueAccess = <ThrowOnError extends boolean = false>(
  options: Options<GetBulkIssueAccessData, ThrowOnError>
) =>
  (options.client ?? client).post<
    GetBulkIssueAccessResponses,
    GetBulkIssueAccessErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/access/_bulk',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Get permissions for a specific issue
 *
 * Retrieve issue permissions and optionally the inherited ACL for a specific issue within a project context.
 *
 * **Required action:** `issue:Read`.
 *
 * - Returns `404` for FILE contexts — permissions are not supported for file-context issues.
 * - ACL keys in the response are limited to `"edit"` and `"view"` — the `"full"` key is internal only and never returned.
 * - `edit` bucket grants: `issue:Read`, `issue:Update`, `issue:AddLinks`, `issue:DeleteLinks`, `issue:ReadLinks`, `issue:UpdatePermissions` (no Delete).
 * - `view` bucket grants: `issue:Read`, `issue:ReadLinks`.
 * - Include `?fields=inheritedAcl` to also receive the computed inherited ACL (creator in edit, assignees in edit).
 */
export const getIssuePermissions = <ThrowOnError extends boolean = false>(
  options: Options<GetIssuePermissionsData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetIssuePermissionsResponses,
    GetIssuePermissionsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/permissions',
    ...options,
  });

/**
 * Update permissions for a specific issue
 *
 * Full-replace PATCH of the explicit ACL for a specific issue within a project context.
 *
 * **Required action:** `issue:UpdatePermissions` in `_actions`.
 *
 * - Returns `404` for FILE contexts — permissions are not supported for file-context issues.
 * - **Full-replace semantics**: the request body replaces the entire explicit ACL; sending an empty map `{}` clears all explicit ACL entries (valid).
 * - Last-write-wins — no optimistic locking.
 * - **Allowed ACL keys**: `"edit"` and `"view"` only. Sending `"full"` returns `400 INVALID_PARAM`.
 * - Identity format: `users:{tid}` or `tc-groups:{id}` or `tc-groups:*` (wildcard = all project members).
 * - Maximum 100 identities across all buckets combined.
 * - `edit` bucket grants: `issue:Read`, `issue:Update`, `issue:AddLinks`, `issue:DeleteLinks`, `issue:ReadLinks`, `issue:UpdatePermissions` (no Delete).
 * - `view` bucket grants: `issue:Read`, `issue:ReadLinks`.
 */
export const patchIssuePermissions = <ThrowOnError extends boolean = false>(
  options: Options<PatchIssuePermissionsData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    PatchIssuePermissionsResponses,
    PatchIssuePermissionsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/permissions',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List default icons
 *
 * Retrieve a list of default icons supported by the service. These icons cannot be deleted or modified. Pagination is not implemented as the default icon set is small.
 */
export const getDefaultIcons = <ThrowOnError extends boolean = false>(
  options?: Options<GetDefaultIconsData, ThrowOnError>
) =>
  (options?.client ?? client).get<
    GetDefaultIconsResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/issues/icons/defaults',
    ...options,
  });

/**
 * Get default issue configuration
 *
 * Retrieve the default configuration settings
 */
export const getDefaultConfiguration = <ThrowOnError extends boolean = false>(
  options?: Options<GetDefaultConfigurationData, ThrowOnError>
) =>
  (options?.client ?? client).get<
    GetDefaultConfigurationResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/issues/configuration/defaults',
    ...options,
  });

/**
 * Get user access information for a specific issue
 *
 * Retrieve the user access information (effective `_actions`) for a specific issue within a specified context.
 *
 * - The `_actions` array reflects the active restriction mode at call time.
 * - Supports checking access for the authenticated caller or for a specific actor (requires APP_ADMIN access when `actorId` is supplied).
 * - PROJECT context restriction ON: actors with no ACL match receive an empty `_actions` array.
 * - FILE context: returns all actions except `issue:UpdatePermissions`.
 */
export const getIssueAccessById = <ThrowOnError extends boolean = false>(
  options: Options<GetIssueAccessByIdData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetIssueAccessByIdResponses,
    GetIssueAccessByIdErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/access',
    ...options,
  });

/**
 * List all issues in a context (snapshot)
 *
 * Retrieve all issues in a context ordered by `updatedAt`. Used for initial sync.
 *
 * Returns minimal information by default. Paginate using `links.next`. Issues may repeat if updated during sync.
 *
 * **Visibility rules (same as listIssues):**
 * - PROJECT context restriction ON: only issues for which the caller has `issue:Read` are returned; others are silently omitted.
 * - PROJECT context restriction OFF: all issues in the context are returned.
 * - FILE context: all issues in the context are returned.
 */
export const listObjectsInContext = <ThrowOnError extends boolean = false>(
  options: Options<ListObjectsInContextData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListObjectsInContextResponses,
    ListObjectsInContextErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/snapshot',
    ...options,
  });

/**
 * List all link objects for a context
 *
 * Retrieve all issues links in a context, ordered by updatedAt. Used for initial sync. Returns minimal information by default.Paginate using `links.next`. Issues may repeat if updated during sync.
 */
export const getLinkObjects = <ThrowOnError extends boolean = false>(
  options: Options<GetLinkObjectsData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetLinkObjectsResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/links/snapshot',
    ...options,
  });

/**
 * List recently changed links in a context
 *
 * Retrieve all links in a context that were created or updated since a given change token. This API should be used after calling the objects API to get incremental changes. You can include deleted links as well.
 */
export const getLinkChanges = <ThrowOnError extends boolean = false>(
  options: Options<GetLinkChangesData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetLinkChangesResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/links/changes',
    ...options,
  });

/**
 * List changes to issues in a context
 *
 * Retrieve a list of changed issues since a `changeToken`, ordered by `updatedAt`. Used for incremental sync. Minimal info by default. Paginate with `links.next`. Issues may repeat if updated during sync.
 *
 * **Visibility rules (same as listIssues):**
 * - PROJECT context restriction ON: only issues for which the caller has `issue:Read` are returned; others are silently omitted.
 * - PROJECT context restriction OFF: all changed issues are returned.
 * - FILE context: all changed issues are returned.
 */
export const listChangesInContext = <ThrowOnError extends boolean = false>(
  options: Options<ListChangesInContextData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListChangesInContextResponses,
    ListChangesInContextErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/changes',
    ...options,
  });

/**
 * Delete a reaction
 *
 * Delete a specific reaction by its ID
 */
export const deleteReaction = <ThrowOnError extends boolean = false>(
  options: Options<DeleteReactionData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteReactionResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/{issueId}/reactions/{reactionId}',
    ...options,
  });

/**
 * Delete an icon for a context
 *
 * Delete a specific icon from the given context.
 */
export const deleteIconById = <ThrowOnError extends boolean = false>(
  options: Options<DeleteIconByIdData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteIconByIdResponses,
    unknown,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/context/{contextId}/issues/icons/{iconId}',
    ...options,
  });
